'use client';

import { fetchCustomerAddress } from '@/api/account';
import { emailUs } from '@/api/customer';
import { trackOrder as apiTrackOrder } from '@/api/order';
import { IOrder as Order } from '@/api/types/order';
import ActionSheet from '@/components/ActionSheet';
import Button from '@/components/Button';
import Cascader from '@/components/Cascader';
import Dialog from '@/components/Dialog';
import EmailInput from '@/components/Form/EmailInput';
import FormInput from '@/components/Form/FormInput';
import PhoneInput from '@/components/Form/PhoneInput';
import Select from '@/components/Form/Select';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { toast } from '@/components/Toast';
import Upload from '@/components/Upload';
import { useStore } from '@/providers/app-store-provider';
import { useAppCommunication } from '@/providers/app-communication-provider';
import { env } from '@/utils/env';
import { formatPhoneNoByRule } from '@/utils/form-rule';
import { Box, Chip, MenuItem } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ErrorCircleOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useGoodsSelect } from '../hooks/useGoodsSelect';
import styles from './ContactForm.module.scss';
import GoodsSelect from './GoodsSelect';
import OrderSelector from './OrderSelector';
import { PhoneListComponent } from './PhoneList';
import logger from '@/utils/client-logger';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';

import { useTranslations } from 'next-intl';
/* eslint-disable */

// 定义表单数据类型，基于Vue文件中的form对象结构
interface FormData {
  name: string;
  email: string;
  telephone: string; // 改为字符串类型，匹配PhoneInput组件
  description: string;
  subject: (string | number)[]; // 级联选择器返回路径数组
  order_no: string;
  product_skus: string[];
  order_item_ids: number[];
  images: any[];
  refund_method: string;
}

interface ContactFormProps {
  subjectOptions: any;
  originSolutionOptions: any;
  isMobile?: boolean;
}

export default function ContactForm({
  subjectOptions,
  originSolutionOptions,
  isMobile,
}: ContactFormProps) {
  const t = useTranslations('contact-zeelool.ContactForm');
  const userInfo = useStore((state) => state.userInfo);
  const countryList = useStore((state) => state.countryList);
  const token = useStore((state) => state.token);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const router = useRouter();
  const { isUserInfoReady } = useAppCommunication();
  // 在本页面判断是否在 App 内嵌环境，避免从 hook 获取的延迟
  const { deviceType } = useContext(ResponsiveContext);
  const isInApp = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  // 构建电话号码的默认值，对应Vue中的逻辑
  const getDefaultPhoneValue = useMemo(() => {
    if (userInfo?.telephone && userInfo?.country_id && userInfo?.tel_code) {
      return `${userInfo.country_id}(+${userInfo.tel_code}) ${userInfo.telephone}`;
    }
    return `${env.NEXT_PUBLIC_LOCALE_DEFAULT_TELCODE} `; // 默认当前国家的区号
  }, [userInfo]);

  // 定义默认值，对应Vue文件中的初始form数据
  const defaultFormValues: FormData = {
    name: userInfo?.name || '', // 从用户信息初始化name
    email: userInfo?.email || '', // 从用户信息初始化email
    telephone: getDefaultPhoneValue, // 使用计算出的默认电话值
    description: '',
    subject: [], // 级联选择器的路径数组
    order_no: '',
    product_skus: [],
    order_item_ids: [],
    images: [],
    refund_method: '',
  };

  // 初始化表单
  const methods = useForm<FormData>({
    defaultValues: defaultFormValues,
    mode: 'onChange', // 实时校验
  });

  const {
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, isSubmitting },
  } = methods;

  // 状态管理，对应Vue文件中的data
  const [submitLoading, setSubmitLoading] = useState(false);
  const [hiddenFormItem, setHiddenFormItem] = useState(['order', 'sku']);
  const [requiredItem, setRequiredItem] = useState(['name', 'email', 'subject', 'message']);
  const [messagePlaceholder, setMessagePlaceholder] = useState('');
  const [isTrackOrder, setIsTrackOrder] = useState(false);
  const [issueArticleList, setIssueArticleList] = useState<any[]>([]);
  const [solutionOptions, setSolutionOptions] = useState<any[]>([]);
  const [solutionPlaceholderMsg, setSolutionPlaceholderMsg] = useState<Record<string, string>>({});
  const [isDelivered, setIsDelivered] = useState(2);
  const [workType, setWorkType] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);

  const [orderTrack, setOrderTrack] = useState<{
    track_number?: string;
    shipment_number?: string;
    logistics_channel_href?: string;
    agent_logistics_code?: string;
    order_ships?: Array<{ id: number; content: string; created_at: string }>;
    head_ships?: Array<{ id: number; content: string; created_at: string }>;
    order_node_val?: Array<{ id: number; content: string; created_at: string }>;
  }>({});

  // 文件上传相关状态
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ url: string; type: string }>>([]);

  const [orderStatus, setOrderStatus] = useState('');
  const [isMoreThan30, setIsMoreThan30] = useState(false);
  const [oldMessagePlaceholder, setOldMessagePlaceholder] = useState('');

  // Order相关状态
  const [showOrderSelector, setShowOrderSelector] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const phoneInputRef = useRef<HTMLInputElement>(null);

  const [address, setAddress] = useState([]);
  const [phoneList, setPhoneList] = useState<any[]>([]);
  const [phoneListOpen, setPhoneListOpen] = useState(false);
  const [phoneListAnchorEl, setPhoneListAnchorEl] = useState<HTMLElement | null>(null);
  const [isPhoneListClosing, setIsPhoneListClosing] = useState(false);

  const [showSolution, setShowSolution] = useState(false);

  useEffect(() => {
    if (phoneListOpen) {
      setPhoneListAnchorEl(phoneInputRef.current);
    } else {
      setPhoneListAnchorEl(null);
    }
  }, [phoneListOpen]);

  const {
    showGoodsSelector,
    setShowGoodsSelector,
    selectedGoods,
    setSelectedGoods,
    skuList,
    loading: goodsLoading,
    loadGoodsFromOrder,
  } = useGoodsSelect();

  // 监听subject变化，对应Vue中的watch逻辑
  const selectedSubject = watch('subject');
  const solutionSelectedValue = watch('refund_method');

  //处理表单值subject的变化
  useEffect(() => {
    console.log('selectedSubject', selectedSubject);
    if (selectedSubject && selectedSubject.length > 0) {
      let item: any = { children: subjectOptions };
      selectedSubject.forEach((value) => {
        if (item?.children) {
          item = item?.children?.find((el: any) => el.value === value);
        }
      });

      // 更新相关配置，对应Vue中的逻辑
      setHiddenFormItem(item?.hiddenFormItem || ['order', 'sku']);
      setIssueArticleList(item?.issueArticleList || []);
      setRequiredItem(item?.requiredItem || ['name', 'email', 'subject', 'message']);
      setMessagePlaceholder(item?.messagePlaceholder || '');
      setIsTrackOrder(item?.isTrackOrder || false);
      setSolutionOptions(item?.solutionOptions || []);
      setSolutionPlaceholderMsg(item?.solutionPlaceholderMsg || {});
      setIsDelivered(item?.isDelivered || 2);
      setWorkType(item?.workType || '');
      setOldMessagePlaceholder(item?.messagePlaceholder || '');
      setValue('refund_method', '');
    } else {
      // 重置状态
      setHiddenFormItem(['order', 'sku']);
      setIssueArticleList([]);
      setRequiredItem(['name', 'email', 'subject', 'message']);
      setMessagePlaceholder('');
      setIsTrackOrder(false);
      setSolutionOptions([]);
      setSolutionPlaceholderMsg({});
      setIsDelivered(2);
      setWorkType('');
      setNoticeMessage('');
      setOrderTrack({});
      setUploadedFiles([]);
      setOldMessagePlaceholder('');
      setValue('refund_method', '');
    }

    if (hiddenFormItem.includes('order')) {
      setValue('order_no', '');
      setValue('product_skus', []);
    }

    if (hiddenFormItem.includes('sku')) {
      setValue('product_skus', []);
    }
    // if (isTrackOrder && selectedOrder?.id) {
    //   trackOrder(selectedOrder.id);
    // }

    // 清除验证错误（如果有的话）
    setTimeout(() => {
      clearErrors();
    }, 0);
  }, [selectedSubject]);

  // 处理订单跟踪
  useEffect(() => {
    if (isTrackOrder && selectedOrder?.id) {
      trackOrder(selectedOrder.id);
    }
  }, [isTrackOrder, selectedOrder]);

  //处理地址和电话
  useEffect(() => {
    // 如果是 app 内嵌环境，需要等待用户信息交互完成之后再执行
    if (isInApp && !isUserInfoReady) {
      logger.log('[ContactForm] App内嵌环境，等待用户信息准备完成，跳过执行');
      return;
    }
    if (userInfo && token) {
      //设置 form 的值
      setValue('name', userInfo.name);
      setValue('email', userInfo.email);
      getAddress();
      if (userInfo.telephone) {
        const tempPhoneList = [];
        tempPhoneList.push({
          label: `${userInfo.country_id}(+${userInfo.tel_code}) ${
            userInfo.country_id === 'US'
              ? formatPhoneNoByRule(userInfo.telephone, '(###)###-####')
              : userInfo.telephone
          }`,
          phoneNo:
            userInfo.country_id === 'US'
              ? formatPhoneNoByRule(userInfo.telephone, '(###)###-####')
              : userInfo.telephone,
          areaNo: `${userInfo.country_id}(+${userInfo.tel_code})`,
        });

        setPhoneList(tempPhoneList);
      }
    } else {
      // 用户未登录，清空表单字段
      setValue('name', '');
      setValue('email', '');
      setValue('telephone', `${env.NEXT_PUBLIC_LOCALE_DEFAULT_TELCODE} `);
      setPhoneList([]);
      setAddress([]);
    }
  }, [userInfo, token, isUserInfoReady, isInApp]);

  useEffect(() => {
    if (address.length > 0) {
      const list =
        address?.map((item) => ({
          label: `${item.country_id}(+${item.tel_code}) ${item.country_id === 'US' ? formatPhoneNoByRule(item.telephone, '(###)###-####') : item.telephone}`,
          phoneNo:
            item.country_id === 'US'
              ? formatPhoneNoByRule(item.telephone, '(###)###-####')
              : item.telephone,
          areaNo: `${item.country_id}(+${item.tel_code})`,
        })) || [];

      setPhoneList(
        //@ts-ignore TODO:为什么这样使用？
        Array.from(new Set([...phoneList, ...list].map(JSON.stringify))).map(JSON.parse),
      );
    }
  }, [address]);

  // 验证附件字段
  useEffect(() => {
    if (requiredItem.includes('file') && uploadedFiles.length === 0) {
      // 如果附件是必填但没有上传文件，设置验证错误
      if (uploadedFiles.length === 0) {
        // 这里可以用setError来设置验证错误，但由于react-hook-form的限制，我们在UI中直接检查
      }
    }
  }, [requiredItem, uploadedFiles]);

  const getAddress = async () => {
    try {
      const response = await fetchCustomerAddress();
      if (response?.code === 200 && response?.data) {
        setAddress(response.data.list || []);
      }
    } catch (error) {
      logger.error('获取地址列表失败:', error);
      setAddress([]);
    }
  };

  // 处理订单选择输入框点击
  const handleOrderInputClick = () => {
    if (!token) {
      // 未登录，显示登录提示
      setShowLoginRegisterDialog({
        show: true,
        situation: 'Contact Us查订单',
      });
      return;
    }
    // 已登录，打开订单选择器
    setShowOrderSelector(true);
  };

  // 处理订单选择
  const handleOrderSelect = (order: Order | null) => {
    setSelectedOrder(order);
    // 订单切换时，清空已选商品
    setSelectedGoods([]);
    if (order) {
      // 计算30天逻辑，对应Vue中的selectOrder watch逻辑
      const moreThan30 = isMoreThan30Days(order.delivered_at);
      setIsMoreThan30(moreThan30);
      setOrderStatus(order.status || '');

      setValue('order_no', order.order_no);
      // 重置商品选择
      setValue('product_skus', []);
      setValue('order_item_ids', []);

      // 立即触发验证，清除错误状态
      trigger('order_no');
      trigger('product_skus');

      // 如果符合特定条件，重置solution和message，对应Vue中的逻辑
      if (isDelivered === 1 && order.status === 'delivery' && moreThan30) {
        setValue('refund_method', '');
        setValue('description', '');
        setMessagePlaceholder(oldMessagePlaceholder);
      }
    } else {
      setValue('order_no', '');
      setValue('product_skus', []);
      setValue('order_item_ids', []);
      // 触发验证以显示错误状态
      trigger('order_no');
      trigger('product_skus');
      // 重置通知消息和跟踪数据
      setNoticeMessage('');
      setOrderTrack({});
      setOrderStatus('');
      setIsMoreThan30(false);
    }
  };

  const refundMethod = watch('refund_method');
  // 监听refund_method变化，对应Vue中的'form.refund_method'的watch
  useEffect(() => {
    if (refundMethod) {
      setMessagePlaceholder(solutionPlaceholderMsg[refundMethod] || '');
    } else {
      setMessagePlaceholder(oldMessagePlaceholder);
    }
  }, [refundMethod]);

  // 步骤展示组件
  const TrackSteps = ({
    steps,
    title,
    active = true,
  }: {
    steps: Array<{ id: number; content: string; created_at: string }>;
    title: string;
    active?: boolean;
  }) => (
    <div className="mb-6">
      <div
        className={`track-section-title inline-flex rounded-[12px] mb-4 py-2 px-12 text-sm font-medium ${
          active ? 'bg-primary-purple text-white' : 'bg-[#a9a9a9] text-white'
        }`}
      >
        {title}
      </div>
      <div className="relative p-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-start mb-4 last:mb-0 gap-8">
            {/* 时间轴点 */}
            <div className="flex flex-col items-center mr-4">
              <div
                className={`w-14 h-14 rounded-full ${active && index === 0 ? 'bg-primary-purple' : 'bg-[#a9a9a9]'} flex-shrink-0`}
              />
              {index < steps.length - 1 && (
                <div className={`w-0.5 h-64 md:h-60 bg-[#a9a9a9] mt-2`} />
              )}
            </div>
            {/* 步骤内容 */}
            <div
              className={`flex-1 border-b border-gray-300 pb-8 md:pb-16 ${index === 0 ? 'text-primary-purple' : 'text-[#a9a9a9]'}`}
            >
              <div className="text-sm mb-1">{step.created_at}</div>
              <div className="text-sm ">{step.content}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // 复制功能
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // TODO: 显示复制成功提示
      console.log('Copied to clipboard:', text);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // 订单跟踪函数
  const trackOrder = async (orderId: number | string) => {
    try {
      setTrackLoading(true);
      console.log('Track order:', orderId);

      const res = await apiTrackOrder({
        order_id: orderId,
        page: 'contact_us',
      });

      // 处理API响应数据，按照Vue代码中的逻辑
      const responseData: any = res.data || {};
      const newOrderTrack: any = {};

      if (responseData.order_number) {
        newOrderTrack.order_number = responseData.order_number;
      }
      if (responseData.agent_logistics_code) {
        newOrderTrack.agent_logistics_code = responseData.agent_logistics_code;
      }
      if (responseData.logistics_channel_href) {
        newOrderTrack.logistics_channel_href = responseData.logistics_channel_href;
      }
      if (responseData.shipment_number) {
        newOrderTrack.shipment_number = responseData.shipment_number;
      }
      if (responseData.track_number) {
        newOrderTrack.track_number = responseData.track_number;
      }
      if (responseData.order_ships?.length) {
        newOrderTrack.order_ships = responseData.order_ships;
      }
      if (responseData.head_ships?.length) {
        newOrderTrack.head_ships = responseData.head_ships;
      }
      if (responseData.order_node_val?.length) {
        newOrderTrack.order_node_val = responseData.order_node_val;
      }

      setOrderTrack(newOrderTrack);
      setNoticeMessage(responseData.notice_message || '');
    } catch (error) {
      console.error('Track order failed:', error);
      setNoticeMessage('');
      setOrderTrack({});
    } finally {
      setTrackLoading(false);
    }
  };

  // 监听订单变化，加载对应的商品列表
  useEffect(() => {
    if (selectedOrder) {
      loadGoodsFromOrder(selectedOrder);
    }
  }, [selectedOrder]);

  // 处理商品选择
  const handleGoodsSelect = (goods: any[]) => {
    setSelectedGoods(goods);
    // 更新表单值
    setValue(
      'product_skus',
      goods.map((item) => item.shop_sku),
    );
    setValue(
      'order_item_ids',
      goods.map((item) => item.id),
    );
    // 立即触发验证，清除错误状态
    trigger('product_skus');
  };

  // 处理文件上传
  const handleFileUpload = (fileUrls: string | string[]) => {
    // 将上传的文件URL转换为需要的格式
    const urls = Array.isArray(fileUrls) ? fileUrls : [fileUrls];
    const files = urls.map((url) => ({
      url,
      type: getFileTypeFromUrl(url),
    }));

    setUploadedFiles(files);
    setValue('images', files);

    // 手动触发验证，检查是否满足必填要求
    if (requiredItem.includes('file')) {
      setTimeout(() => {
        if (methods.clearErrors && methods.trigger) {
          // 如果有文件则清除错误，否则触发验证
          if (files.length > 0) {
            methods.clearErrors('images');
          } else {
            methods.trigger('images');
          }
        }
      }, 0);
    }
  };

  // 根据文件URL获取文件类型
  const getFileTypeFromUrl = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase();
    const typeMap: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    return typeMap[extension || ''] || 'application/octet-stream';
  };

  // 判断是否超过30天，对应Vue中的isMoreThan30Days方法
  const isMoreThan30Days = (dateString: string | null | undefined): boolean => {
    if (!dateString) return true;

    try {
      // 处理 iOS 日期解析问题（空格替换为T）
      const formattedDate = dateString.replace(' ', 'T');
      const targetDate = new Date(formattedDate);
      const currentDate = new Date();

      // 计算天数差（向下取整）
      const daysDiff = Math.floor(
        (currentDate.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24),
      );
      return daysDiff > 30;
    } catch {
      return true;
    }
  };

  // 处理解决方案选项过滤，对应Vue中的handleDealSolutionOptions方法
  const handleDealSolutionOptions = (
    options: string[],
  ): Array<{ key_name: string; english_name: string }> => {
    const realOptions: Array<{ key_name: string; english_name: string }> = [];

    originSolutionOptions.forEach((item) => {
      if (options.includes(item.key_name)) {
        realOptions.push(item);
      }
    });

    return realOptions;
  };
  const handleMobileDealSolutionOptions = (
    options: string[],
  ): Array<{ label: string; value: string }> => {
    const realOptions: Array<{ label: string; value: string }> = [];

    originSolutionOptions.forEach((item) => {
      if (options.includes(item.key_name)) {
        realOptions.push({
          value: item.key_name,
          label: item.english_name,
        });
      }
    });

    return realOptions;
  };

  // 提交成功后弹窗提示
  const handleSubmitDialogClick = () => {
    setSubmitDialogOpen(false);
    router.push(token ? '/account/tickets-history' : '/');
  };

  // 提交表单处理函数，对应Vue文件中的submit方法
  const onSubmit = async (data: FormData) => {
    console.log('Form submitted:', data);

    // 检查附件必填验证
    if (requiredItem.includes('file') && uploadedFiles.length === 0) {
      console.error('Attachments are required');
      // 显示错误提示
      if (methods.setError) {
        methods.setError('images', {
          type: 'required',
          message: 'Attachments are required',
        });
      }
      return;
    }

    setSubmitLoading(true);

    try {
      // 处理电话号码格式，对应Vue中的逻辑
      let telephone = '';
      if (data.telephone && data.telephone.trim()) {
        // 提取区号和号码
        const phoneMatch = data.telephone.match(/^(.+?\(\+\d+\))\s*(.*)$/);
        if (phoneMatch && phoneMatch[2]) {
          const areaCode = phoneMatch[1]; // 如: US(+1)
          const phoneNumber = phoneMatch[2]; // 电话号码部分

          if (phoneNumber.trim()) {
            // 按照Vue中的格式化逻辑：'(' + form.telephone.split('(')[1]
            telephone = `${areaCode}${phoneNumber}`;
            telephone = '(' + telephone.split('(')[1];
          }
        }
      }

      // 构建提交数据，对应Vue中的submit逻辑
      const formData = {
        name: data.name,
        email: data.email,
        telephone: telephone || undefined,
        description: data.description,
        subject: data.subject.join(' / '),
        order_no: hiddenFormItem.includes('order') ? undefined : data.order_no,
        product_skus: hiddenFormItem.includes('sku') ? undefined : data.product_skus,
        order_item_ids: hiddenFormItem.includes('sku') ? undefined : data.order_item_ids,
        images: hiddenFormItem.includes('file')
          ? undefined
          : uploadedFiles.length > 0
            ? uploadedFiles.map((file) => ({
                url: file.url,
                type: file.type,
              }))
            : undefined,
        refund_method: data.refund_method || undefined,
        work_type: workType || undefined,
      };

      const response = await emailUs(formData);
      if (response.code === 200) {
        setSubmitDialogOpen(true);
      } else {
        toast.error(response.msg);
      }
    } catch (error) {
      console.error('提交失败:', error);
      toast.error(t('failedToSendEmail'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePhoneApply = (item: any) => {
    setValue('telephone', `${item.areaNo}${item.phoneNo}`);
    setPhoneListOpen(false);
  };

  // 防止删除默认邮箱的键盘事件处理器
  const handleEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // 只有当用户有默认邮箱时才启用防删除逻辑
    if (!userInfo?.email) return;

    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const defaultEmail = userInfo.email;

    // 如果当前值等于默认邮箱，且用户试图删除
    if (currentValue === defaultEmail && (event.key === 'Backspace' || event.key === 'Delete')) {
      event.preventDefault();
      return;
    }

    // 如果当前值为空或只有空白字符，且用户试图删除
    if (!currentValue.trim() && (event.key === 'Backspace' || event.key === 'Delete')) {
      event.preventDefault();
      return;
    }
  };

  // 防止删除默认邮箱的输入变化处理器
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 只有当用户有默认邮箱时才启用防删除逻辑
    if (!userInfo?.email) return;

    const newValue = event.target.value;
    const defaultEmail = userInfo.email;

    // 如果新值为空或只有空白字符，恢复默认值
    if (!newValue.trim()) {
      setValue('email', defaultEmail);
      return;
    }
  };

  // 错误处理，自动滚动到第一个错误字段
  const onError = (errors: any) => {
    console.log('表单验证错误:', errors);

    // 定义字段优先级顺序（按表单显示顺序）
    const fieldPriority = [
      'name',
      'email',
      'telephone',
      'subject',
      'order_no',
      'product_skus',
      'refund_method',
      'description',
      'images',
    ];

    // 按优先级查找第一个错误字段
    let firstErrorField = '';
    for (const field of fieldPriority) {
      if (errors[field]) {
        firstErrorField = field;
        break;
      }
    }

    // 如果没找到，使用第一个错误字段
    if (!firstErrorField) {
      firstErrorField = Object.keys(errors)[0];
    }

    if (firstErrorField) {
      // 尝试多种选择器来定位字段
      const selectors = [
        `[name="${firstErrorField}"]`,
        `input[name="${firstErrorField}"]`,
        `textarea[name="${firstErrorField}"]`,
        `[data-field="${firstErrorField}"]`,
        `.${firstErrorField}-field`,
      ];

      let fieldElement: Element | null = null;

      for (const selector of selectors) {
        fieldElement = document.querySelector(selector);
        if (fieldElement) break;
      }

      // 特殊处理某些字段
      if (!fieldElement) {
        switch (firstErrorField) {
          case 'subject':
            fieldElement =
              document.querySelector('[name="subjectString"]') ||
              document.querySelector('.cascader-field');
            break;
          case 'order_no':
            fieldElement =
              document.querySelector('[name="order"]') || document.querySelector('.order-field');
            break;
          case 'product_skus':
            fieldElement =
              document.querySelector('[name="product_skus"]') ||
              document.querySelector('.product-field');
            break;
          case 'refund_method':
            fieldElement =
              document.querySelector('[name="refund_method_string"]') ||
              document.querySelector('.solution-field');
            break;
          case 'images':
            fieldElement =
              document.querySelector('.attachments-field') ||
              document.querySelector('.upload-area');
            break;
        }
      }

      if (fieldElement) {
        // 滚动到字段位置
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        // 尝试聚焦到输入框
        const inputElement = fieldElement.querySelector('input, textarea, select') as HTMLElement;
        if (inputElement && typeof inputElement.focus === 'function') {
          setTimeout(() => {
            inputElement.focus();
          }, 300);
        }
      } else {
        console.warn(`Could not find element for field: ${firstErrorField}`);
      }
    }
  };

  // 渲染solution
  const renderPcSolution = () => (
    <div className="solution-field mb-[23px]">
      <label
        className={`block text-sm font-medium text-gray-700 mb-2 ${
          requiredItem.includes('solution') ? 'after:content-["*"] after:text-error after:ml-1' : ''
        }`}
      >
        {t('Solution')}&nbsp;
      </label>
      <Select
        className="group"
        name="refund_method"
        control={methods.control}
        placeholder={t('solutionPlaceholder')}
        rules={{
          required: requiredItem.includes('solution') ? t('solutionRequired') : false,
        }}
        formItemClassName="mb-0"
        endAdornment={
          solutionSelectedValue && (
            <div
              className={`group-has-[:hover]:block hidden absolute right-18 bg-white cursor-pointer text-[16px] text-gray-400`}
              onClick={() => setValue('refund_method', '')}
            >
              <ErrorCircleOutlined />
            </div>
          )
        }
      >
        {handleDealSolutionOptions(solutionOptions).map((option) => (
          <MenuItem key={option.key_name} value={option.english_name}>
            {option.english_name}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
  const renderMobileSolution = () => (
    <div className="solution-field mb-[23px]">
      <FormInput
        className="group"
        label="Solution *"
        name="refund_method"
        layout="vertical"
        control={methods.control}
        placeholder={t('solutionPlaceholder')}
        rules={{
          required: requiredItem.includes('solution') ? t('solutionRequired') : false,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
        formItemClassName="mb-0"
        onClick={() => setShowSolution(true)}
        endIcon={
          <RightArrowOutlined
            className={`text-[16px] text-gray-400 ${isMobile ? 'rotate-0' : 'rotate-90'}`}
          />
        }
      />
      <ActionSheet
        value={solutionSelectedValue}
        open={showSolution}
        onClose={() => setShowSolution(false)}
        onCancel={() => setShowSolution(false)}
        onSave={(value, option) => {
          setValue('refund_method', option.label);
          clearErrors('refund_method');
        }}
        saveText="Confirm"
        cancelText="Cancel"
        title="Solution"
        options={handleMobileDealSolutionOptions(solutionOptions)}
      />
    </div>
  );

  return (
    <div className={`contact-form ${isMobile ? styles['mobile'] : ''}`}>
      <FormProvider {...methods}>
        <div className="space-y-6">
          {/* 表单项将在后续添加 */}
          <div className="form-content">
            {/* Name 字段 */}
            <FormInput
              name="name"
              control={methods.control}
              label={
                <p>
                  {t('Name')} <span className="text-error">*</span>
                </p>
              }
              layout="vertical"
              placeholder={t('namePlaceholder')}
              slotProps={{
                htmlInput: {
                  maxLength: 34,
                },
              }}
              autoComplete="off"
              rules={{
                required: requiredItem.includes('name') ? t('nameRequired') : false,
              }}
              required
              formItemClassName="mb-[23px]"
            />

            {/* Email 字段 */}
            <EmailInput
              name="email"
              control={methods.control}
              label={
                <p>
                  {t('Email')} <span className="text-error">*</span>
                </p>
              }
              layout="vertical"
              placeholder={t('emailPlaceholder')}
              slotProps={{
                htmlInput: {
                  maxLength: 50,
                },
              }}
              required
              clearable={!userInfo?.email} // 如果有默认邮箱，禁用清除按钮
              onKeyDown={handleEmailKeyDown}
              onChange={handleEmailChange}
              disabled={!!userInfo?.email}
              rules={{
                required: requiredItem.includes('email') ? t('emailRequired') : false,
                pattern: {
                  value: /^[0-9a-zA-Z_.-]+[@][0-9a-zA-Z_.-]+([.][a-zA-Z]+){1,2}$/,
                  message: t('validEmailRequired'),
                },
              }}
              formItemClassName="mb-[23px]"
            />

            {/* Phone 字段 - 条件显示 */}
            {!hiddenFormItem.includes('phone') && (
              <>
                <WebOnly>
                  <ClickAwayListener onClickAway={() => setPhoneListOpen(false)}>
                    <PhoneInput
                      ref={phoneInputRef}
                      name="telephone"
                      control={methods.control}
                      label={t('phone')}
                      layout="vertical"
                      placeholder={t('phonePlaceholder')}
                      defaultValue={getDefaultPhoneValue}
                      countryList={countryList || []}
                      formItemClassName="mb-[23px]"
                      required={false}
                      onFocus={() => {
                        // 防止弹窗关闭时立即重新打开
                        if (!isPhoneListClosing && phoneList.length > 0) {
                          setPhoneListOpen(true);
                        }
                      }}
                    />
                  </ClickAwayListener>
                </WebOnly>
                <WapOnly>
                  <PhoneInput
                    ref={phoneInputRef}
                    name="telephone"
                    control={methods.control}
                    label={t('phone')}
                    layout="vertical"
                    placeholder={t('phonePlaceholder')}
                    defaultValue={getDefaultPhoneValue}
                    countryList={countryList || []}
                    formItemClassName="mb-[23px]"
                    required={false}
                    onClick={() => {
                      phoneList.length > 0 && setPhoneListOpen(true);
                    }}
                  />
                </WapOnly>
              </>
            )}

            {/* Subject 字段 */}
            <div data-field="subject" className="subject-field">
              <Cascader
                name="subject"
                control={methods.control}
                label={
                  <p>
                    {t('Subject')} <span className="text-error">*</span>
                  </p>
                }
                layout="vertical"
                // required
                placeholder={t('pleaseSelectSubject')}
                options={subjectOptions}
                separator=" / "
                showAllLevels={true}
                clearable={true}
                rules={{
                  required: requiredItem.includes('subject') ? t('subjectRequired') : false,
                }}
                formItemClassName="mb-[23px]"
              />
            </div>

            {/* 常见问题推荐 - 根据选择的主题显示 */}
            {issueArticleList.length > 0 && (
              <div className="suggest-article border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {t('commonlyAskedQuestions')}
                </h3>
                {issueArticleList.map((item, index) => (
                  <div key={index} className="py-3">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-orange-500 underline text-base"
                    >
                      {item.title}
                    </a>
                  </div>
                ))}
              </div>
            )}

            {/* Order 字段 - 条件显示 */}
            {!hiddenFormItem.includes('order') && (
              <div className="flex space-y-16 md:space-y-0 mb-[23px] flex-col md:flex-row md:space-x-4">
                <div className="flex-1" data-field="order_no">
                  <FormInput
                    name="order_no"
                    control={methods.control}
                    label={
                      <p>
                        {t('Order')} <span className="text-error">*</span>
                      </p>
                    }
                    layout="vertical"
                    placeholder={t('orderPlaceHolder')}
                    slotProps={{
                      htmlInput: {
                        maxLength: 20,
                        readOnly: true,
                      },
                    }}
                    onClick={handleOrderInputClick}
                    rules={{
                      required: requiredItem.includes('order') ? t('orderRequired') : false,
                    }}
                    formItemClassName="mb-0"
                    required
                    endIcon={
                      isMobile ? (
                        <RightArrowOutlined
                          className={`text-[16px] text-gray-400 ${isMobile ? 'rotate-0' : 'rotate-90'}`}
                        />
                      ) : undefined
                    }
                  />
                </div>
                {/* Product字段 - 如果有订单号且不隐藏sku */}
                {selectedOrder && !hiddenFormItem.includes('sku') && (
                  <div className="flex-1" data-field="product_skus">
                    <FormInput
                      name="product_skus"
                      control={methods.control}
                      label={
                        <p>
                          {t('Product')} <span className="text-error">*</span>
                        </p>
                      }
                      layout="vertical"
                      placeholder={t('pleaseSelectProducts')}
                      value={
                        selectedGoods.length > 0
                          ? `Selected ${selectedGoods.length} product(s)`
                          : ''
                      }
                      slotProps={{
                        htmlInput: {
                          readOnly: true,
                        },
                        input: {
                          startAdornment: (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                gap: 0.5,
                                mr: 1, // 与输入文本保持间距
                                alignItems: 'center',
                                maxWidth: 'calc(100% - 20px)', // 限制最大宽度，避免溢出
                              }}
                            >
                              {!!selectedGoods.length && (
                                <Chip
                                  label={selectedGoods[0].shop_sku}
                                  onDelete={() => {
                                    const currentGoodId = selectedGoods[0].id;
                                    const newGoods = [];
                                    for (const item of selectedGoods) {
                                      if (item.id !== currentGoodId) {
                                        newGoods.push(item);
                                      }
                                    }
                                    setSelectedGoods(newGoods);
                                  }}
                                  size="small"
                                  sx={{
                                    '& .MuiChip-deleteIcon': {
                                      fontSize: '14px',
                                    },
                                  }}
                                />
                              )}
                              {selectedGoods.length > 1 && (
                                <Chip
                                  label={`+${selectedGoods.length - 1}`}
                                  size="small"
                                  sx={{
                                    '& .MuiChip-deleteIcon': {
                                      fontSize: '14px',
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          ),
                        },
                      }}
                      onClick={() => setShowGoodsSelector(true)}
                      rules={{
                        required: requiredItem.includes('sku') ? t('productRequired') : false,
                      }}
                      formItemClassName="mb-0"
                      required
                      sx={{
                        '& .MuiInputBase-input': {
                          visibility: 'hidden',
                        },
                      }}
                      endIcon={
                        <RightArrowOutlined
                          className={`text-[16px] text-gray-400 ${isMobile ? 'rotate-0' : 'rotate-90'}`}
                        />
                      }
                    />
                  </div>
                )}
              </div>
            )}

            {/* Solution 字段 - 复杂条件显示 WAP TODO: 两端位置不一样，建议统一 */}
            {!hiddenFormItem.includes('solution') &&
              solutionOptions.length > 0 &&
              // selectedOrder && // 👈 如果需要订单号条件，取消注释这行
              (isDelivered === 2 ||
                (isDelivered === 1 && orderStatus !== 'delivery') ||
                (isDelivered === 1 && orderStatus === 'delivery' && !isMoreThan30)) &&
              isMobile &&
              renderMobileSolution()}

            {/* 订单跟踪通知消息 - 条件显示 */}
            {isTrackOrder && selectedOrder && noticeMessage && (
              <div className="track-notice-message mb-[23px] p-3 bg-amber-50 border-l-4 border-amber-400 text-amber-800 text-sm leading-relaxed">
                {noticeMessage}
              </div>
            )}

            {/* 订单跟踪详细信息 - 条件显示 */}
            {isTrackOrder && selectedOrder && (
              <div className="express-track md:max-h-[460px] overflow-y-auto mb-[23px] border border-gray-200 rounded-lg p-12 md:p-20">
                {trackLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <p className="text-gray-500 mt-2">{t('loadingTrackingInformation')}</p>
                  </div>
                ) : (
                  <>
                    {/* 跟踪号码和物流渠道 */}
                    {(orderTrack.track_number ||
                      orderTrack.shipment_number ||
                      (orderTrack.logistics_channel_href && orderTrack.agent_logistics_code)) && (
                      <div className="track-order-top mb-6 p-4">
                        {(orderTrack.track_number || orderTrack.shipment_number) && (
                          <div className="relative track-order-no flex items-center mb-3">
                            <div className="text-sm font-bb text-[16px]">
                              <span>{t('trackNo')} </span>
                              <br className="md:hidden" />
                              <span className="font-medium">
                                {orderTrack.track_number || orderTrack.shipment_number}
                              </span>
                            </div>
                            <button
                              className="absolute bottom-0 right-0 copy-btn px-3 py-1 text-xs text-primary-purple text-[14px] font-br underline cursor-pointer ml-6"
                              onClick={() =>
                                copyToClipboard(
                                  orderTrack.track_number || orderTrack.shipment_number || '',
                                )
                              }
                            >
                              {t('copy')}
                            </button>
                          </div>
                        )}

                        {orderTrack.logistics_channel_href && orderTrack.agent_logistics_code && (
                          <div className="track-order-channel text-sm">
                            <span className="text-gray-600">{t('logisticsChannel')}: </span>
                            <a
                              href={orderTrack.logistics_channel_href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              {orderTrack.agent_logistics_code}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 物流步骤 */}
                    <div className="tracking-steps">
                      {orderTrack.order_ships && orderTrack.order_ships.length > 0 && (
                        <TrackSteps steps={orderTrack.order_ships} title="After" active={true} />
                      )}

                      {orderTrack.head_ships && orderTrack.head_ships.length > 0 && (
                        <TrackSteps
                          steps={orderTrack.head_ships}
                          title="First"
                          active={!orderTrack.order_ships || orderTrack.order_ships.length === 0}
                        />
                      )}

                      {orderTrack.order_node_val && orderTrack.order_node_val.length > 0 && (
                        <TrackSteps
                          steps={orderTrack.order_node_val}
                          title="Process Information"
                          active={!orderTrack.order_ships && !orderTrack.head_ships}
                        />
                      )}

                      {/* 无信息提示 */}
                      {!orderTrack.order_ships &&
                        !orderTrack.head_ships &&
                        !orderTrack.order_node_val && (
                          <p className="text-center text-gray-500 text-lg py-8">
                            {t('noInformationAvailable')}
                          </p>
                        )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Solution 字段 - 复杂条件显示 PC TODO: 两端位置不一样，建议统一 */}
            {!hiddenFormItem.includes('solution') &&
              solutionOptions.length > 0 &&
              // selectedOrder && // 👈 如果需要订单号条件，取消注释这行
              (isDelivered === 2 ||
                (isDelivered === 1 && orderStatus !== 'delivery') ||
                (isDelivered === 1 && orderStatus === 'delivery' && !isMoreThan30)) &&
              !isMobile &&
              renderPcSolution()}

            {/* Message 字段 */}
            <FormInput
              id="outlined-multiline-static"
              name="description"
              control={methods.control}
              label={
                <p>
                  {t('Message')} <span className="text-error">*</span>
                </p>
              }
              layout="vertical"
              placeholder={messagePlaceholder}
              multiline
              rows={4}
              rules={{
                required: requiredItem.includes('message') ? t('messageRequired') : false,
              }}
              required={requiredItem.includes('message')}
              formItemClassName="mb-[23px]"
              sx={{
                '& .MuiInputBase-root': {
                  height: '120px',
                },
              }}
            />

            {/* Attachments 字段 - 条件显示 */}
            {!hiddenFormItem.includes('file') && (
              <div className="attachments-field mb-[23px]" data-field="images">
                <div className="flex items-center">
                  <label
                    className={`block text-[16px] text-primary-text font-br  ${
                      requiredItem.includes('file')
                        ? 'after:content-["*"] after:text-red-500 after:ml-1'
                        : ''
                    }`}
                  >
                    {t('Attachments')}
                  </label>
                  {isMobile && (
                    <span className="text-sm text-secondary-text ml-8 block">
                      ({t('attachmentsLimited')})
                    </span>
                  )}
                </div>

                <Upload
                  sceneId={5} // Email附件场景ID
                  fileType={isMobile ? 'image' : 'all'} // 移动端只支持图片，PC端支持所有文件
                  accept={
                    isMobile
                      ? 'image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png'
                      : '.xlsx,.xls,.doc,.docx,.jpeg,.jpg,.png,.pdf'
                  }
                  multiple={true}
                  maxSize={10} // 10MB限制
                  maxCount={5} // 最多5个文件
                  showImage={isMobile} // 显示文件预览
                  enableDrag={!isMobile} // PC端支持拖拽，移动端不支持
                  className="w-full"
                  showFileList={!isMobile}
                  onChange={handleFileUpload}
                >
                  {/* 自定义上传区域内容 */}
                  {!isMobile && (
                    <div className="upload-area w-full min-h-[120px]  border-2 border-solid border-gray-300 rounded-lg flex  items-center justify-center p-24  transition-colors cursor-pointer">
                      <div className="upload-icon mb-12">
                        <img
                          src="/zeelool-next/contact-zeelool/upload-file.png"
                          alt="Upload"
                          className="w-32 h-32 mr-12"
                        />
                      </div>
                      <div className="upload-text text-center">
                        <p className="text-left mb-4">{t('dragAndDropFilesHere')} </p>
                        <p className="text-auxiliary-text text-sm">{t('attachmentsLimited')}</p>
                      </div>
                    </div>
                  )}
                </Upload>

                {/* 文件上传的表单验证 */}
                {requiredItem.includes('file') && uploadedFiles.length === 0 && (
                  <div className="text-red-500 text-sm mt-1">{t('attachmentsAreRequired')}</div>
                )}
              </div>
            )}
          </div>

          {/* 提交按钮 */}
          <Button
            onClick={handleSubmit(onSubmit, onError)}
            loading={submitLoading}
            className="w-full  px-32 py-16 rounded-lg font-bb text-white"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
              color: 'white',
              fontSize: '16px',
              borderRadius: '10px',
              ...(!isMobile && {
                width: '460px',
                height: '64px',
              }),
              ...(isMobile && {
                height: '48px',
              }),
            }}
          >
            {t('submit')}
          </Button>
          {/* 提交成功后的弹窗 */}
          {!isMobile ? (
            <Dialog.Confirm
              open={submitDialogOpen}
              onClose={handleSubmitDialogClick}
              onOk={handleSubmitDialogClick}
              okText="Back"
              title="Submitted successfully"
              sx={
                isMobile
                  ? {
                      '& .MuiDialogTitle-root': {
                        fontSize: '16px',
                      },
                      '& .MuiDialogContent-root': {
                        color: '#808080',
                      },
                      '& .MuiDialogActions-root button': {
                        fontSize: '16px',
                        width: '220px',
                        margin: '0 auto',
                        borderRadius: '24px',
                      },
                    }
                  : {
                      '& .MuiDialogTitle-root': {
                        marginTop: '20px',
                        fontSize: '24px',
                      },
                      '& .MuiDialogTitle-root button': {
                        top: 0,
                      },
                      '& .MuiDialogContent-root': {
                        fontSize: '18px',
                        lineHeight: '24px',
                        paddingLeft: '40px',
                        paddingRight: '40px',
                      },
                      '& .MuiDialogActions-root': {
                        fontSize: '16px',
                        width: '350px',
                        margin: '0 auto',
                      },
                    }
              }
            >
              <div>{t('submitSuccessDesc')}</div>
            </Dialog.Confirm>
          ) : (
            <Popup
              open={submitDialogOpen}
              position="bottom"
              showDivider={false}
              onClose={handleSubmitDialogClick}
              onOk={handleSubmitDialogClick}
              height="76vh"
              title="Submitted successfully"
              okText={'Back'}
              hasFooter={true}
              sx={{
                '& .popup-content': {
                  color: '#808080',
                },
              }}
            >
              <div>{t('submitSuccessDesc')}</div>
            </Popup>
          )}
        </div>
      </FormProvider>

      {/* 订单选择器弹窗 */}
      <OrderSelector
        open={showOrderSelector}
        onClose={() => setShowOrderSelector(false)}
        onSelect={handleOrderSelect}
        selectedOrder={selectedOrder}
      />

      {/* 商品选择器弹窗 */}
      <GoodsSelect
        open={showGoodsSelector}
        onClose={() => setShowGoodsSelector(false)}
        onSelect={handleGoodsSelect}
        selectedGoods={selectedGoods}
        skuList={skuList}
        loading={goodsLoading}
      />

      <PhoneListComponent
        open={phoneListOpen}
        telephoneData={methods.getValues('telephone')}
        anchorEl={phoneListAnchorEl}
        phoneList={phoneList}
        handleApply={handlePhoneApply}
        onPhoneChange={(newTelephoneData) => {
          methods.setValue('telephone', newTelephoneData);
        }}
        onClose={() => {
          setPhoneListOpen(false);
        }}
      />
    </div>
  );
}
